// Is there a live media session on this agent's softphone right now?
//
// WHY THIS EXISTS
// The switch is the only thing that tells us a call ended, and it tells us over a
// socket that can deliver a teardown late, twice, or for a leg we never registered.
// When that happens mid-conversation the UI flips to WRAPUP and — within the default
// 10s auto-wrapup window — writes an AUTOWRAP disposition against a call the agent is
// still on. The browser's own SIP session is the one signal that cannot lie about
// this: if the media session is Established, the call IS up, whatever the socket says.
//
// That signal was previously thrown away. `incomingInvitationRef` is nulled the moment
// `accept()` resolves, and the auto-answer path never attached a state listener at all,
// so nothing in the app could answer "is the agent still talking?". This module keeps
// the answer available.
//
// WHY MODULE-LEVEL (the same reasoning as utils/breakState.js)
// There is exactly one UserAgent per app, so session liveness is genuinely global —
// and its two consumers live in different trees: CallingSidebar (which owns the SIP
// stack) and pages/CRM/Crm.jsx (which owns the auto-submit that must not fire during a
// live call). A ref cannot span those. Unlike Redux state this is deliberately NOT
// persisted: a stale `live` surviving a reload into localStorage would block CRM
// submission with no call to end. Module scope resets on reload, which is correct.

// The sip.js Session for the call currently up, or null. Compared by string so this
// module stays free of a sip.js import and unit-testable on its own.
let activeSession = null;
// Have we ever tracked a session in this page's lifetime? Distinguishes "the call
// ended" from "we never had visibility", which callers must treat differently.
let sawSession = false;
// False for agents on a desk phone (`web_phone === 0`) — there is no browser session
// to read, so liveness is permanently unknowable and must never block anything.
let softphoneEnabled = true;

const endedSubscribers = new Set();

const isEstablished = (session) => session?.state === 'Established';

export const setSoftphoneEnabled = (enabled) => {
  softphoneEnabled = !!enabled;
};

export const setActiveSession = (session) => {
  if (!session) return;
  activeSession = session;
  sawSession = true;
};

// Drop the tracked session and wake anything waiting on the call to really end.
// Safe to call with a stale session: a late Terminated for a previous call must not
// clear the session of the call that has already replaced it.
export const clearActiveSession = (session) => {
  if (session && activeSession && session !== activeSession) return;
  const had = !!activeSession;
  activeSession = null;
  if (!had) return;
  endedSubscribers.forEach((cb) => {
    try {
      cb();
    } catch (error) {
      console.log('mediaLiveness subscriber error', error);
    }
  });
};

// Called when the media session ends. Used to release a teardown that was held back
// because the socket claimed the call was over while the media said otherwise.
export const subscribeSessionEnded = (cb) => {
  endedSubscribers.add(cb);
  return () => endedSubscribers.delete(cb);
};

// 'live'    — a media session is established right now. Do not wrap up, do not submit.
// 'ended'   — we were tracking a session and it is gone. Safe to proceed immediately.
// 'unknown' — we cannot tell (desk phone, or no session ever captured).
//
// CALLERS MUST TREAT 'unknown' AS "DO NOT BLOCK". Blocking on it would break every
// desk-phone agent, whose calls never produce a browser session at all.
export const getMediaLiveness = () => {
  if (!softphoneEnabled) return 'unknown';
  if (isEstablished(activeSession)) return 'live';
  if (sawSession) return 'ended';
  return 'unknown';
};

// Logout / teardown.
export const resetMediaLiveness = () => {
  activeSession = null;
  sawSession = false;
  softphoneEnabled = true;
  endedSubscribers.clear();
};
